import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Header from "../../components/Header";
import api from "../../services/api";
import { showSuccess, showError } from "../../services/alert";
import LocationPicker from "../../components/LocationPicker";
import "./PropriedadeForm.css";

const PropriedadeForm = () => {
  const { id } = useParams();
  const isEditing = Boolean(id && id !== "create");
  const navigate = useNavigate();

  const [agricultores, setAgricultores] = useState([]);
  const [estados, setEstados] = useState([]);
  const [cidades, setCidades] = useState([]);
  const [loadingLocation, setLoadingLocation] = useState(false);
  const [loadingMapPreview, setLoadingMapPreview] = useState(false);
  const [locationMode, setLocationMode] = useState("cidade");
  const [mapPreview, setMapPreview] = useState({ latitude: "", longitude: "" });
  const [form, setForm] = useState({ agricultorId: "", nome: "", areaHa: "", estado: "", cidade: "", latitude: "", longitude: "", ativo: true });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/agricultores")
      .then(({ data }) => setAgricultores(data || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    fetch("https://servicodados.ibge.gov.br/api/v1/localidades/estados?orderBy=nome")
      .then(response => response.json())
      .then(data => setEstados(Array.isArray(data) ? data : []))
      .catch(() => showError("Não foi possível carregar os estados."));
  }, []);

  useEffect(() => {
    if (!form.estado) { setCidades([]); return; }
    setLoadingLocation(true);
    fetch(`https://servicodados.ibge.gov.br/api/v1/localidades/estados/${form.estado}/municipios?orderBy=nome`)
      .then(response => response.json())
      .then(data => setCidades(Array.isArray(data) ? data : []))
      .catch(() => showError("Não foi possível carregar as cidades."))
      .finally(() => setLoadingLocation(false));
  }, [form.estado]);

  useEffect(() => {
    if (isEditing) {
      setLoading(true);
      api.get(`/propriedades/${id}`)
        .then(({ data }) => {
          setForm({
            agricultorId: data.agricultorId,
            nome: data.nome,
            areaHa: data.areaHa,
            estado: data.estado ?? "",
            cidade: data.cidade ?? "",
            latitude: data.latitude ?? "",
            longitude: data.longitude ?? "",
            ativo: data.ativo,
          });
          setLocationMode(data.estado && data.cidade ? "cidade" : data.latitude != null && data.longitude != null ? "coordenadas" : "cidade");
          setMapPreview({ latitude: data.latitude ?? "", longitude: data.longitude ?? "" });
        })
        .catch(() => showError("Erro ao carregar registro."))
        .finally(() => setLoading(false));
    }
  }, [id, isEditing]);

  useEffect(() => {
    if (locationMode !== "cidade" || !form.estado || !form.cidade) return undefined;

    const controller = new AbortController();
    setLoadingMapPreview(true);
    api.get("/geocodificacao", { params: { cidade: form.cidade, estado: form.estado }, signal: controller.signal })
      .then(response => response.data)
      .then(data => {
        if (data?.latitude == null || data?.longitude == null) return;
        setMapPreview({ latitude: Number(data.latitude), longitude: Number(data.longitude) });
      })
      .catch(error => {
        if (error.name !== "AbortError") setMapPreview({ latitude: "", longitude: "" });
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoadingMapPreview(false);
      });

    return () => controller.abort();
  }, [form.cidade, form.estado, locationMode]);

  const handleChange = (e) => setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  const handleToggle = (e) => setForm(prev => ({ ...prev, ativo: e.target.checked }));
  const handleEstado = (e) => {
    setMapPreview({ latitude: "", longitude: "" });
    setForm(prev => ({ ...prev, estado: e.target.value, cidade: "" }));
  };
  const handleLocation = (latitude, longitude) => {
    setMapPreview({ latitude, longitude });
    setLocationMode("mapa");
    setForm(prev => ({ ...prev, estado: "", cidade: "", latitude, longitude }));
  };
  const handleCidade = (e) => setForm(prev => ({ ...prev, cidade: e.target.value }));
  const changeLocationMode = (mode) => {
    setLocationMode(mode);
    setForm(prev => {
      if (mode === "cidade") {
        setMapPreview({ latitude: "", longitude: "" });
        return { ...prev, latitude: "", longitude: "" };
      }
      setMapPreview({ latitude: prev.latitude, longitude: prev.longitude });
      return { ...prev, estado: "", cidade: "" };
    });
  };

  const handleCoordinate = (field, value) => {
    setForm(prev => {
      const next = { ...prev, [field]: value };
      setMapPreview({ latitude: next.latitude, longitude: next.longitude });
      return next;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.agricultorId) { showError("Selecione um Agricultor."); return; }
    if (!form.nome.trim()) { showError('O campo "Nome" é obrigatório.'); return; }
    if (form.areaHa === "" || Number(form.areaHa) < 0) { showError("Informe a Área (ha) válida."); return; }
    if (locationMode === "cidade" && Boolean(form.estado) !== Boolean(form.cidade)) { showError("Selecione o estado e a cidade ou deixe os dois campos vazios."); return; }
    if (locationMode !== "cidade" && Boolean(form.latitude !== "") !== Boolean(form.longitude !== "")) { showError("Informe a latitude e a longitude juntas."); return; }
    if (form.latitude !== "" && (Number(form.latitude) < -90 || Number(form.latitude) > 90)) { showError("Latitude deve ficar entre -90 e 90."); return; }
    if (form.longitude !== "" && (Number(form.longitude) < -180 || Number(form.longitude) > 180)) { showError("Longitude deve ficar entre -180 e 180."); return; }

    const payload = {
      id: isEditing ? Number(id) : 0,
      agricultorId: Number(form.agricultorId),
      nome: form.nome.trim(),
      areaHa: Number(form.areaHa),
      estado: locationMode === "cidade" ? form.estado || null : null,
      cidade: locationMode === "cidade" ? form.cidade || null : null,
      latitude: locationMode === "cidade" || form.latitude === "" ? null : Number(form.latitude),
      longitude: locationMode === "cidade" || form.longitude === "" ? null : Number(form.longitude),
      ativo: form.ativo
    };

    setLoading(true);
    try {
      if (isEditing) {
        await api.put(`/propriedades/${id}`, payload);
        showSuccess("Propriedade atualizada com sucesso.");
      } else {
        await api.post("/propriedades", payload);
        showSuccess("Propriedade criada com sucesso.");
      }
      navigate("/propriedades");
    } catch (err) {
      showError(err?.response?.data?.message || "Erro ao salvar.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container mt-4">
      <Header />
      <div className="card shadow-lg border-0 rounded-3">
        <div className="card-header bg-success text-white d-flex align-items-center gap-3 rounded-top-3">
          <button
            className="btn btn-light btn-sm d-flex align-items-center gap-1"
            onClick={() => navigate("/propriedades")}
            disabled={loading}
          >
            <i className="bi bi-arrow-left text-success" />
            <span className="text-success fw-semibold">Voltar</span>
          </button>
          <h5 className="mb-0">
            {isEditing ? "Editar Propriedade" : "Nova Propriedade"}
          </h5>
        </div>

        <div className="card-body">
          <form onSubmit={handleSubmit} className="row g-4">
            <fieldset className="border p-3 rounded-3">
              <legend className="px-3 text-success fw-semibold">Dados da Propriedade</legend>

              <div className="col-md-6">
                <label className="form-label fw-semibold">Agricultor *</label>
                <select
                  name="agricultorId"
                  value={form.agricultorId}
                  onChange={handleChange}
                  className="form-select rounded-3"
                  required
                  disabled={loading}
                >
                  <option value="">Selecione...</option>
                  {(agricultores || []).map(a => (
                    <option key={a.id} value={a.id}>{a.nome}</option>
                  ))}
                </select>
              </div>

              <div className="col-md-6">
                <label className="form-label fw-semibold">Nome *</label>
                <input
                  type="text"
                  name="nome"
                  value={form.nome}
                  onChange={handleChange}
                  className="form-control rounded-3"
                  maxLength={160}
                  required
                  disabled={loading}
                />
              </div>

              <div className="col-md-6">
                <label className="form-label fw-semibold">Área (ha) *</label>
                <input
                  type="number"
                  name="areaHa"
                  step="0.01"
                  min="0"
                  value={form.areaHa}
                  onChange={handleChange}
                  className="form-control rounded-3"
                  required
                  disabled={loading}
                />
              </div>

              <div className="col-12 propriedade-location">
                <div className="location-heading">
                  <div><i className="bi bi-geo-alt-fill" /><span><strong>Onde fica a propriedade?</strong><small>Use somente a forma que for mais prática para você.</small></span></div>
                  {((locationMode === "cidade" && form.estado && form.cidade) || (locationMode !== "cidade" && form.latitude !== "" && form.longitude !== "")) && <span className="location-confirmed"><i className="bi bi-check-circle-fill" /> Localização informada</span>}
                </div>
                <div className="location-mode-selector" role="group" aria-label="Forma de informar a localização">
                  <button type="button" className={locationMode === "cidade" ? "active" : ""} onClick={() => changeLocationMode("cidade")}><i className="bi bi-buildings" /><span>Estado e cidade<small>Escolha pela lista</small></span></button>
                  <button type="button" className={locationMode === "coordenadas" ? "active" : ""} onClick={() => changeLocationMode("coordenadas")}><i className="bi bi-geo" /><span>Coordenadas<small>Digite latitude e longitude</small></span></button>
                  <button type="button" className={locationMode === "mapa" ? "active" : ""} onClick={() => changeLocationMode("mapa")}><i className="bi bi-map" /><span>Selecionar no mapa<small>Marque o ponto visualmente</small></span></button>
                </div>
                {locationMode === "cidade" && <div className="row g-3 location-mode-content">
                  <div className="col-md-5"><label className="form-label fw-semibold">Estado</label><select className="form-select" value={form.estado} onChange={handleEstado} disabled={loading || loadingLocation}><option value="">Selecione o estado</option>{estados.map(estado => <option key={estado.id} value={estado.sigla}>{estado.nome}</option>)}</select></div>
                  <div className="col-md-7"><label className="form-label fw-semibold">Cidade</label><select name="cidade" className="form-select" value={form.cidade} onChange={handleCidade} disabled={loading || loadingLocation || !form.estado}><option value="">{loadingLocation ? "Carregando cidades..." : "Selecione a cidade"}</option>{cidades.map(cidade => <option key={cidade.id} value={cidade.nome}>{cidade.nome}</option>)}</select></div>
                  <small className="location-help"><i className="bi bi-info-circle" /> Nesta opção, não é necessário informar coordenadas.</small>
                </div>}
                {locationMode === "coordenadas" && <div className="row g-3 location-mode-content">
                  <div className="col-md-6"><label className="form-label fw-semibold">Latitude</label><input type="number" step="0.000001" min="-90" max="90" className="form-control" value={form.latitude} onChange={(e) => handleCoordinate("latitude", e.target.value)} placeholder="Ex.: -15.601400" disabled={loading} /></div>
                  <div className="col-md-6"><label className="form-label fw-semibold">Longitude</label><input type="number" step="0.000001" min="-180" max="180" className="form-control" value={form.longitude} onChange={(e) => handleCoordinate("longitude", e.target.value)} placeholder="Ex.: -56.097900" disabled={loading} /></div>
                  <small className="location-help"><i className="bi bi-info-circle" /> Use valores decimais. Estado e cidade não são necessários nesta opção.</small>
                </div>}
                {locationMode === "mapa" && <div className="location-mode-content location-map-instructions"><i className="bi bi-cursor-fill" /><span>Toque no mapa para marcar a propriedade ou use a localização do seu dispositivo.</span></div>}
                <div className="location-map-section">
                  <div className="location-map-title">
                    <span><i className="bi bi-map" /><strong>Confira a localização no mapa</strong></span>
                    {loadingMapPreview && <small><i className="bi bi-arrow-repeat location-spin" /> Localizando cidade...</small>}
                  </div>
                  <LocationPicker latitude={mapPreview.latitude} longitude={mapPreview.longitude} onChange={handleLocation} disabled={loading} locationLabel="Usar localização do dispositivo" />
                  <small className="location-help"><i className="bi bi-info-circle" /> {locationMode === "cidade" ? "O mapa mostra a região da cidade selecionada. Se você ajustar o ponto no mapa, o cadastro passará automaticamente a usar coordenadas." : "O ponto do mapa acompanha as coordenadas e pode ser ajustado a qualquer momento."}</small>
                </div>
              </div>

              {isEditing && (
                <div className="col-md-6 d-flex align-items-end">
                  <div className="form-check form-switch">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      role="switch"
                      checked={form.ativo}
                      onChange={handleToggle}
                      disabled={loading}
                    />
                    <label className="form-check-label ms-2">Ativo</label>
                  </div>
                </div>
              )}

              <div className="col-12 d-flex justify-content-end mt-3">
                <button type="submit" className="btn btn-success me-2 d-flex align-items-center gap-1" disabled={loading}>
                  <i className="bi bi-save" />
                  <span>{loading ? "Salvando..." : "Salvar"}</span>
                </button>
                <button type="button" className="btn btn-secondary d-flex align-items-center gap-1" onClick={() => navigate("/propriedades")} disabled={loading}>
                  <i className="bi bi-x-circle" />
                  <span>Cancelar</span>
                </button>
              </div>
            </fieldset>
          </form>
        </div>
      </div>
    </div>
  );
};

export default PropriedadeForm;
